import { useState, useEffect } from 'react';
import styles from './ImageCarousel.module.css';

interface ImageCarouselProps {
  images: string[];
  maxImages?: number;
}

export function ImageCarousel({ images, maxImages = 3 }: ImageCarouselProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalIndex, setModalIndex] = useState(0);
  const displayImages = images.slice(0, maxImages);
  const totalImages = images.length;

  // Cerrar modal con ESC
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isModalOpen) {
        setIsModalOpen(false);
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isModalOpen]);

  if (!images || images.length === 0) {
    return <span className={styles.noImages}>Sin imágenes</span>;
  }

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? displayImages.length - 1 : prev - 1));
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev === displayImages.length - 1 ? 0 : prev + 1));
  };

  const goToSlide = (index: number) => {
    setCurrentIndex(index);
  };

  const openModal = () => {
    // Mapear el índice del carrusel al índice real del array completo
    // Si estamos mostrando solo las primeras 3, currentIndex ya corresponde al índice real
    setModalIndex(currentIndex);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const goToPreviousModal = () => {
    setModalIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const goToNextModal = () => {
    setModalIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  return (
    <div className={styles.carousel}>
      <div className={styles.carouselContainer}>
        {displayImages.length > 1 && (
          <button
            className={styles.carouselButton}
            onClick={goToPrevious}
            aria-label="Imagen anterior"
          >
            ‹
          </button>
        )}
        
        <div className={styles.imageWrapper}>
          <img
            src={displayImages[currentIndex]}
            alt={`Imagen ${currentIndex + 1} del restaurante`}
            className={styles.carouselImage}
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="60" height="60"%3E%3Crect width="60" height="60" fill="%23ddd"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%23999" font-size="10"%3EError%3C/text%3E%3C/svg%3E';
            }}
          />
          {totalImages > maxImages && (
            <div className={styles.imageCounter}>
              +{totalImages - maxImages}
            </div>
          )}
          <button
            className={styles.viewButton}
            onClick={openModal}
            aria-label="Ver imagen completa"
            title="Ver imagen completa"
          >
            Ver
          </button>
        </div>

        {displayImages.length > 1 && (
          <button
            className={styles.carouselButton}
            onClick={goToNext}
            aria-label="Imagen siguiente"
          >
            ›
          </button>
        )}
      </div>

      {displayImages.length > 1 && (
        <div className={styles.dots}>
          {displayImages.map((_, index) => (
            <button
              key={index}
              className={`${styles.dot} ${index === currentIndex ? styles.activeDot : ''}`}
              onClick={() => goToSlide(index)}
              aria-label={`Ir a imagen ${index + 1}`}
            />
          ))}
        </div>
      )}

      {/* Modal para ver imagen ampliada */}
      {isModalOpen && (
        <div className={styles.modalOverlay} onClick={closeModal}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <button
              className={styles.modalCloseButton}
              onClick={closeModal}
              aria-label="Cerrar"
            >
              ×
            </button>
            {images.length > 1 && (
              <>
                <button
                  className={styles.modalNavButton}
                  onClick={goToPreviousModal}
                  aria-label="Imagen anterior"
                >
                  ‹
                </button>
                <button
                  className={`${styles.modalNavButton} ${styles.modalNavButtonRight}`}
                  onClick={goToNextModal}
                  aria-label="Imagen siguiente"
                >
                  ›
                </button>
              </>
            )}
            <img
              src={images[modalIndex]}
              alt={`Imagen ${modalIndex + 1} del restaurante (ampliada)`}
              className={styles.modalImage}
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="400"%3E%3Crect width="400" height="400" fill="%23ddd"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%23999" font-size="16"%3EError al cargar imagen%3C/text%3E%3C/svg%3E';
              }}
            />
            {images.length > 1 && (
              <div className={styles.modalCounter}>
                {modalIndex + 1} / {images.length}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

